import { ElectronicBill, ElectronicBillFormSchema, ItemComplete, ScheduleForm } from '../../schemas/ElectronicBill'
import ElectronicBillMapper from '../../mappers/ElectronicBill/electronicBill.mapper'
import apiClient from '../apiClient'

class ElectronicBillService {
  /** Saves an electronic bill for the authenticated entity. */
  async saveBill(billData: ElectronicBill) {
    try {
      const responseApi = await apiClient.post('/api/v2/bill/electronic', billData)
      return responseApi.data
    } catch (error) {
      throw error
    }
  }

  /** Lists electronic bills with optional pagination cursor. */
  async getElectronicBills(params?: { limit: number, lastEvaluatedKey?: string }): Promise<{ lastEvaluatedKey: string, electronicBills: ElectronicBillFormSchema[] }> {
    try {
      let url = params !== undefined ? `/api/v2/bill/electronic?limit=${params.limit}` : '/api/v2/bill/electronic'

      if (params !== undefined && params.lastEvaluatedKey !== undefined) {
        url = `${url}&lastEvaluatedKey=${params.lastEvaluatedKey}`
      }

      const responseApi = await apiClient.get(url)

      const bills = responseApi.data.bills.map((bill: any) => {
        const parseBill = ElectronicBillMapper.schemaToForm(bill)
        return parseBill.form
      })

      return {
        lastEvaluatedKey: JSON.stringify(responseApi.data.lastEvaluatedKey),
        electronicBills: bills
      }

    } catch (error) {
      throw error
    }
  }

  /** Loads one electronic bill by invoice number. */
  async getBillByNumber(number: number): Promise<{ form: ElectronicBillFormSchema, items: ItemComplete[], taxes: any[] }> {
    try {
      const responseApi = await apiClient.get(`/api/v2/bill/electronic/${number}`)
      const bill = ElectronicBillMapper.schemaToForm(responseApi.data)
      return bill
    } catch (error) {
      throw error
    }
  }

  /** Lists scheduled bills with optional pagination cursor. */
  async getSchedules(params?: { limit: number, lastEvaluatedKey?: string }): Promise<{ lastEvaluatedKey: string, schedules: ScheduleForm[] }> {
    try {
      let url = params !== undefined ? `/api/v2/bill/electronic/schedule?limit=${params.limit}` : '/api/v2/bill/electronic/schedule'

      if (params !== undefined && params.lastEvaluatedKey !== undefined) {
        url = `${url}&lastEvaluatedKey=${params.lastEvaluatedKey}`
      }

      const responseApi = await apiClient.get(url)

      const schedules = responseApi.data.schedules.map((schedule: any) => {
        return {
          startDate: schedule.startDate ?? '',
          endDate: schedule.endDate ?? 'No',
          name: schedule.name ?? '',
          intervalDays: schedule.intervalDays ?? '',
          entity: schedule.entity ?? '',
          code: schedule.code ?? '',
          idForm: schedule.idForm ?? ''
        }
      })

      return {
        lastEvaluatedKey: JSON.stringify(responseApi.data.lastEvaluatedKey),
        schedules,
      }

    } catch (error) {
      throw error
    }
  }

  /** Deletes a scheduled bill by code. */
  async deleteSchedule(code: string): Promise<any> {
    try {
      await apiClient.delete(`/api/v2/bill/electronic/schedule/${code}`)
    } catch (error) {
      throw error
    }
  }

  /** Returns printable HTML for an electronic invoice. */
  async getElectronicInvoiceHTML(invoiceNumber: number): Promise<string> {
    try {
      const responseApi = await apiClient.get(`/api/v2/bill/electronic/${invoiceNumber}/html`, {
        headers: {
          'Content-Type': 'text/html; charset=utf-8'
        }
      })

      return responseApi.data
    } catch (error) {
      throw error
    }
  }

  /** Returns electronic bills from Plemsi via the backend. */
  async getElectronicBillsFromPlemsi(limit: number) {
    try {
      const responseApi = await apiClient.get('/api/v2/bill/electronic/plemsi', {
        params: {
          limit,
        }
      })
      return responseApi.data.data
    } catch (error) {
      throw error
    }
  }

  /** Returns credit notes from Plemsi via the backend. */
  async getCreditNotesFromPlemsi(page: number) {
    try {
      const responseApi = await apiClient.get('/api/v2/bill/electronic/credit/plemsi', {
        params: {
          page,
        }
      })
      return responseApi.data.data
    } catch (error) {
      throw error
    }
  }

  /** Returns printable HTML for an electronic credit note. */
  async getCreditNoteHTML(cude: string): Promise<string> {
    try {
      const responseApi = await apiClient.get(`/api/v2/bill/electronic/credit/${cude}/html`, {
        headers: {
          'Content-Type': 'text/html; charset=utf-8'
        }
      })

      return responseApi.data
    } catch (error) {
      throw error
    }
  }

  /** Presents a credit note for an electronic invoice through the backend. */
  async presentCreditNote(invoiceNumber: number): Promise<any> {
    try {
      const responseApi = await apiClient.post(`/api/v2/bill/electronic/${invoiceNumber}/present-credit`, null)
      return responseApi.data
    } catch (error) {
      throw error
    }
  }

}

export default ElectronicBillService
